import React, { useMemo } from 'react'

import classnames from 'classnames'

import { isLevelAA } from 'src/shared/js/colorUtils'
import { t } from 'src/translations'

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'

import ChevronIcon from './ChevronIcon'
import { CarouselDirection } from './useCarousel'

type ArrowProps = {
  onClickScroll: (direction: CarouselDirection) => void
  currentItem: number
  whiteInactiveArrow?: boolean
  color: string
  totalScrollItems: number
  borderColor: string
}

export const CarouselArrowLeft = ({
  onClickScroll,
  currentItem,
  whiteInactiveArrow,
  color,
  borderColor
}: ArrowProps) => (
  <button
    aria-label={t('carousel-arrows.scroll-left')}
    className={classnames('carouselArrow left', {
      inactive: currentItem === 0
    })}
    onClick={() => onClickScroll('backwards')}
    data-testid='carousel-left-arrow'
    style={{ borderColor }}
  >
    <ChevronIcon
      color={
        currentItem === 0 ? (whiteInactiveArrow && 'white') || color : color
      }
    />
  </button>
)

export const CarouselArrowRight = ({
  onClickScroll,
  currentItem,
  whiteInactiveArrow,
  color,
  totalScrollItems,
  borderColor
}: ArrowProps) => (
  <button
    aria-label={t('carousel-arrows.scroll-right')}
    className={classnames('carouselArrow right', {
      inactive: currentItem === totalScrollItems - 1
    })}
    onClick={() => onClickScroll('forwards')}
    data-testid='carousel-right-arrow'
    style={{ borderColor }}
  >
    <ChevronIcon
      color={
        currentItem === totalScrollItems - 1
          ? (whiteInactiveArrow && 'white') || color
          : color
      }
    />
  </button>
)

type Props = {
  arrowsRef: React.RefObject<HTMLDivElement>
  color: string
  whiteInactiveArrow?: boolean
  arrowSize?: 'small' | 'large'
  onClickScroll: (direction: CarouselDirection) => void
  currentItem: number
  totalScrollItems: number
  textColor?: string
}

const getContrastingColor = (backgroundColor: string): string => {
  return (
    ['#FFFFFF', '#000000'].find((color) => isLevelAA(backgroundColor, color)) ||
    '#FFFFFF'
  )
}

const CarouselArrows = (props: Props) => {
  const { arrowsRef, arrowSize, ...arrowProps } = props
  const getColorFromTheme = useThemeColorScheme()
  const backgroundColor = getColorFromTheme(
    (theme) => theme.colorScheme.background.primary,
    props.color
  )

  const arrowColor = useMemo(() => {
    const themeIconColor = getColorFromTheme(
      (theme) => theme.colorScheme.icon.default,
      null
    )
    // If theme provides an icon color that has sufficient contrast, use it
    // Otherwise, calculate a contrasting color based on the background
    if (themeIconColor && isLevelAA(backgroundColor, themeIconColor)) {
      return themeIconColor
    }
    return getContrastingColor(backgroundColor)
  }, [getColorFromTheme, backgroundColor])

  const borderColor = useMemo(() => {
    const themeBorderColor = getColorFromTheme(
      (theme) => theme.colorScheme.border.withPrimary,
      null
    )
    // If theme provides a border color that has sufficient contrast, use it
    // Otherwise, calculate a contrasting color based on the background
    if (themeBorderColor && isLevelAA(backgroundColor, themeBorderColor)) {
      return themeBorderColor
    }
    return getContrastingColor(backgroundColor)
  }, [getColorFromTheme, backgroundColor])

  const colorsStyle = props?.textColor ? { color: props.textColor } : {}

  return (
    <div
      className={classnames('carouselArrows', arrowSize)}
      ref={arrowsRef}
      data-testid='carousel-arrows'
    >
      <CarouselArrowLeft
        {...arrowProps}
        borderColor={borderColor}
        color={arrowColor}
      />
      <div
        className='itemRange'
        aria-label={t('carousel-arrows.currently-on-slide', {
          current: arrowProps.currentItem + 1,
          total: arrowProps.totalScrollItems
        })}
        style={colorsStyle}
      >
        {arrowProps.currentItem + 1}
        <span className='itemTotal'> / {arrowProps.totalScrollItems}</span>
      </div>
      <CarouselArrowRight
        {...arrowProps}
        borderColor={borderColor}
        color={arrowColor}
      />
    </div>
  )
}

export default CarouselArrows
